"use client";

import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import styles from "./HomepageHeroImage.module.scss";
import { HomepageHeroImageTop } from "./HomepageHeroImage.types";

type HomepageHeroImageTitleProps = {
  heading: HomepageHeroImageTop["heading"];
  subheading: HomepageHeroImageTop["subheading"];
  buttons: HomepageHeroImageTop["buttons"];
  tabletCentered?: boolean;
  theme?: ThemeTypes;
};

const track = (destination: string) => ({
  destination,
  name: "Marketing CTA Clicked",
  type: "button" as const,
});

const HomepageHeroImageTitle = ({
  heading,
  subheading,
  buttons,
  tabletCentered = false,
  theme,
}: HomepageHeroImageTitleProps) => {
  const { primary, primaryAlt, secondary } = buttons;
  const isMen = theme === "men";
  const TextElement = isMen ? TextElementMen : TextElementNova;
  const align = tabletCentered ? "md:items-center lg:items-start" : "md:items-start";
  const textAlign = tabletCentered ? "md:text-center lg:text-left" : "md:text-left";
  const primaryButton = (
    <ButtonNova icon="Right" trackObject={track(primary.ctaLink)} {...primary} />
  );

  /* Men: one row of Shop + quiz, quiz as an outlined button. */
  const menButtons = (
    <div className="flex justify-center gap-2.5 lg:justify-start lg:gap-4">
      {primaryButton}
      <ButtonNova
        icon="Right"
        level="Secondary"
        onDarkBackground
        trackObject={track(secondary.ctaLink)}
        {...secondary}
      />
    </div>
  );

  const womenButtons = (
    <div className="flex w-full max-w-[327px] flex-col gap-2 md:max-w-[294px] md:gap-4 lg:max-w-[350px]">
      {primaryAlt ? (
        <div className="flex gap-2 lg:gap-4 [&>*]:flex-1">
          {primaryButton}
          <ButtonNova icon="Right" trackObject={track(primaryAlt.ctaLink)} {...primaryAlt} />
        </div>
      ) : (
        primaryButton
      )}
      <ButtonNova
        icon="Right"
        level="Secondary"
        onImageBackground
        trackObject={track(secondary.ctaLink)}
        {...secondary}
      />
    </div>
  );

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center",
        isMen ? "gap-6 md:gap-9 lg:gap-10 xl:gap-20" : "gap-4 lg:gap-10",
        align
      )}
    >
      {/* Men xl: 7px, not 16px, because the 75px title's line box sits lower than Figma's font. */}
      <div
        className={cn(
          "flex w-full flex-col items-center gap-2",
          isMen && "lg:gap-4 xl:gap-[7px]",
          align
        )}
      >
        <TextElement
          element="h1"
          {...heading}
          className={cn(
            "Title-30-Super-Normal max-w-[329px] text-center text-nova-base-white md:max-w-[640px] lg:max-w-[948px]",
            isMen && "uppercase max-w-none md:max-w-none lg:max-w-none",
            textAlign,
            styles.topHeading,
            isMen && styles.topHeadingMen,
            heading.className
          )}
        />
        <TextElement
          element="span"
          {...subheading}
          className={cn("block text-center text-nova-base-white", textAlign, subheading.className)}
        />
      </div>
      {isMen ? menButtons : womenButtons}
    </div>
  );
};

export default HomepageHeroImageTitle;
